import { Injectable } from "@angular/core";
import { BrandingConfigurationService } from "@ptl/brand";
import { HTTPWrapperContext, REQUEST_METHODS, RequestConfigImplementation, XHR_STRATEGIES } from "@ptl/xhr";
import { EnrollmentRequestPayload, EnrollmentResponsePayload, EnrollmentSubmissionResponse } from "../interfaces/mug-submission.interface";
import { HttpHeaders } from "@angular/common/http";
import { SubmittedEnrollmentsResponse, FullSubmissionLogResponse } from "../interfaces/mug-submitted-enrollments.interface";
import { CaseResponse, CaseStatusResponse, CreateCaseResponse } from "../interfaces/mug-case.interface";


@Injectable({
  providedIn: 'root'
})
export class MassUploadGridCaseService {

  submissionLogCache: { [key: string]: FullSubmissionLogResponse } = {};
  dashboardCache: {[key:string]: CaseResponse} = {};

  constructor(
    private httpClient: HTTPWrapperContext,
    private brandService: BrandingConfigurationService
  ) {
    
  }

  clearDashboardCache() {
    this.dashboardCache = {};
  }

  createCaseId(loginId: string, agencyCode: string): Promise<CreateCaseResponse> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: `/sxesvc/api/v1/case`,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
        agencynumber: agencyCode
      }),
      POST_OR_PUT_PAYLOAD: {
        loginId:loginId,
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
        agencyCode: agencyCode
      },
    });
    
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  loadAllCaseStatus(agencyCode: string): Promise<CaseStatusResponse> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: `/sxesvc/api/v1/case/dashboard/counts-by-status`,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
        agencynumber: agencyCode
      })
    });
    
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  loadEnrollmentsForCaseId(caseId: string, agencyCode: string): Promise<EnrollmentResponsePayload> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: `/sxesvc/api/v1/case/${caseId}/enrollments`,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
        agencynumber: agencyCode
      })
    });
    
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  saveEnrollmentsForCaseId(caseId: string, userId: string, agencyCode: string, payload: EnrollmentRequestPayload): Promise<EnrollmentRequestPayload> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: `/sxesvc/api/v1/case/${caseId}/enrollments`,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
        agencynumber: agencyCode,
        loginId: userId
      }),
      POST_OR_PUT_PAYLOAD: payload,
    });
    
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  submitEnrollmentsForCaseId(caseId: string, userId: string, agencyCode: string, payload: EnrollmentRequestPayload): Promise<EnrollmentSubmissionResponse> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: `/sxesvc/api/v1/case/${caseId}/enrollments/submission`,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
        agencynumber: agencyCode,
        loginId: userId
      }),
      POST_OR_PUT_PAYLOAD: payload,
    });
    
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  removeCase(caseId: string, userId: string, agencyCode: string): Promise<void> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.DELETE,
      url: `/sxesvc/api/v1/case/${caseId}`,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
        agencynumber: agencyCode,
        loginId: userId
      })
    });
    
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  downloadExcelForCase(caseId: string, userId: string, agencyCode: string): Promise<void> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: `/sxesvc/api/v1/case/${caseId}/enrollments/download`,
      contentType: 'application/octet-stream',
      accept: 'application/octet-stream',
      responseType: 'blob',
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
        agencynumber: agencyCode,
        loginId: userId
      })
    });
    
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  async fetchCaseSubmissionLogData(caseId: string, agencyCode: string): Promise<FullSubmissionLogResponse> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: `/sxesvc/api/v1/case/${caseId}/enrollments/submitted`,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
        agencynumber: agencyCode
      })
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  async fetchAllSubmissionLogData(agencyCode: string, queryParams: any): Promise<FullSubmissionLogResponse> {
    const cacheKey = JSON.stringify(queryParams);
    if (!this.submissionLogCache[cacheKey]) {
      const requestConfig = new RequestConfigImplementation({
        method: REQUEST_METHODS.GET,
        url: `/sxesvc/api/v1/case/enrollments/submitted`,
        headers: new HttpHeaders({
          ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
          agencynumber: agencyCode
        }),
        GET_PAYLOAD: queryParams
      });

      try {
        this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
        const response = await this.httpClient.makeRequest(requestConfig) as FullSubmissionLogResponse;
        if (response?.results) {
          this.submissionLogCache[cacheKey] = response;
        }
        else {
          throw new Error('Fetch submission log request failed');
        }
      }
      catch (e) {
        console.error(e);
      }
    }

    return this.submissionLogCache[cacheKey];
  }
  
  async fetchCaseData(agencyCode: string, queryParams: { [key: string]: any }): Promise<CaseResponse> {
    let cacheKey = JSON.stringify(queryParams);
    if (!this.dashboardCache[cacheKey]) {
      const requestConfig = new RequestConfigImplementation({
        method: REQUEST_METHODS.GET,
        url: `/sxesvc/api/v1/case/dashboard`,
        headers: new HttpHeaders({
          ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
          agencynumber: agencyCode
        }),
        GET_PAYLOAD: queryParams
      });

      try {
        this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
        let response = await this.httpClient.makeRequest(requestConfig) as CaseResponse;
        if (response?.content) {
          this.dashboardCache[cacheKey] = response;
        }
        else {
          throw new Error('Fetch cases request failed');
        }
      }
      catch(e) {
        console.error(e);
      }
    }

    return this.dashboardCache[cacheKey];
  }
}